/**
 * DisputeTrayContext
 * 
 * Global state management for multi-item dispute selection across tabs.
 * Allows users to select items from different categories (accounts, inquiries, 
 * public records, personal info) and manage them in a unified dispute tray.
 * 
 * FIVE S CLASSIFICATION ENGINE:
 * - Routes disputes directly to the recommended strategy path
 * - Uses backend classification: Isolated, Structural, Recovery, Combo
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import adminOpsService from '../services/adminOpsService';

// Item categories for grouping
export const DISPUTE_CATEGORIES = {
  ACCOUNTS: 'accounts',
  INQUIRIES: 'inquiries',
  PUBLIC_RECORDS: 'public_records',
  PERSONAL_INFO: 'personal_info',
  CREDITOR_CONTACTS: 'creditor_contacts'
};

// Category display labels
export const CATEGORY_LABELS = {
  [DISPUTE_CATEGORIES.ACCOUNTS]: 'Accounts',
  [DISPUTE_CATEGORIES.INQUIRIES]: 'Inquiries',
  [DISPUTE_CATEGORIES.PUBLIC_RECORDS]: 'Public Records',
  [DISPUTE_CATEGORIES.PERSONAL_INFO]: 'Personal Information',
  [DISPUTE_CATEGORIES.CREDITOR_CONTACTS]: 'Creditor Contacts'
};

// Create context
const DisputeTrayContext = createContext(null);

/**
 * DisputeTrayProvider - Wraps the app to provide dispute tray state
 */
export const DisputeTrayProvider = ({ children }) => {
  const { user, getAuthHeaders } = useAuth();
  
  // Selected items by category: { accounts: [], inquiries: [], ... }
  const [selectedItems, setSelectedItems] = useState({
    [DISPUTE_CATEGORIES.ACCOUNTS]: [],
    [DISPUTE_CATEGORIES.INQUIRIES]: [],
    [DISPUTE_CATEGORIES.PUBLIC_RECORDS]: [],
    [DISPUTE_CATEGORIES.PERSONAL_INFO]: [],
    [DISPUTE_CATEGORIES.CREDITOR_CONTACTS]: []
  });
  
  // Tray visibility state
  const [isTrayExpanded, setIsTrayExpanded] = useState(false);
  
  // Bulk dispute wizard visibility
  const [showBulkWizard, setShowBulkWizard] = useState(false);
  
  // Classification state for smart routing
  const [classificationResult, setClassificationResult] = useState(null);
  const [isClassifying, setIsClassifying] = useState(false);
  
  /**
   * Add an item to the dispute tray
   */
  const addItem = useCallback((category, item) => {
    setSelectedItems(prev => {
      const categoryItems = prev[category] || [];
      // Check if already exists (by id)
      const itemId = item.tradeline_id || item.inquiry_id || item.record_id || item.id || item.field;
      if (categoryItems.some(i => (i.tradeline_id || i.inquiry_id || i.record_id || i.id || i.field) === itemId)) {
        return prev;
      }
      return {
        ...prev,
        [category]: [...categoryItems, { ...item, _category: category }]
      };
    });
  }, []);
  
  /**
   * Remove an item from the dispute tray
   */
  const removeItem = useCallback((category, itemId) => {
    setSelectedItems(prev => {
      const categoryItems = prev[category] || [];
      return {
        ...prev,
        [category]: categoryItems.filter(item => {
          const id = item.tradeline_id || item.inquiry_id || item.record_id || item.id || item.field;
          return id !== itemId;
        })
      };
    });
  }, []);
  
  /**
   * Toggle item selection
   */
  const toggleItem = useCallback((category, item) => {
    const itemId = item.tradeline_id || item.inquiry_id || item.record_id || item.id || item.field;
    setSelectedItems(prev => {
      const categoryItems = prev[category] || [];
      const exists = categoryItems.some(i => {
        const id = i.tradeline_id || i.inquiry_id || i.record_id || i.id || i.field;
        return id === itemId;
      });
      
      if (exists) {
        return {
          ...prev,
          [category]: categoryItems.filter(i => {
            const id = i.tradeline_id || i.inquiry_id || i.record_id || i.id || i.field;
            return id !== itemId;
          })
        };
      } else {
        return {
          ...prev,
          [category]: [...categoryItems, { ...item, _category: category }]
        };
      }
    });
  }, []);
  
  /**
   * Check if an item is selected
   */
  const isItemSelected = useCallback((category, itemId) => {
    const categoryItems = selectedItems[category] || [];
    return categoryItems.some(item => {
      const id = item.tradeline_id || item.inquiry_id || item.record_id || item.id || item.field;
      return id === itemId;
    });
  }, [selectedItems]);
  
  /**
   * Clear all items from a category
   */
  const clearCategory = useCallback((category) => {
    setSelectedItems(prev => ({
      ...prev,
      [category]: []
    }));
  }, []);
  
  /**
   * Clear all selected items
   */
  const clearAll = useCallback(() => {
    setSelectedItems({
      [DISPUTE_CATEGORIES.ACCOUNTS]: [],
      [DISPUTE_CATEGORIES.INQUIRIES]: [],
      [DISPUTE_CATEGORIES.PUBLIC_RECORDS]: [],
      [DISPUTE_CATEGORIES.PERSONAL_INFO]: [],
      [DISPUTE_CATEGORIES.CREDITOR_CONTACTS]: []
    });
    setIsTrayExpanded(false);
  }, []);
  
  /**
   * Get count for a specific category
   */
  const getCategoryCount = useCallback((category) => {
    return (selectedItems[category] || []).length;
  }, [selectedItems]);
  
  /**
   * Get total count of all selected items
   */
  const totalCount = useMemo(() => {
    return Object.values(selectedItems).reduce((sum, items) => sum + items.length, 0);
  }, [selectedItems]);
  
  /**
   * Get all selected items as a flat array with category info
   */
  const allSelectedItems = useMemo(() => {
    return Object.entries(selectedItems).flatMap(([category, items]) => 
      items.map(item => ({ ...item, _category: category }))
    );
  }, [selectedItems]);
  
  /**
   * Get category counts object
   */
  const categoryCounts = useMemo(() => {
    return Object.entries(selectedItems).reduce((acc, [category, items]) => {
      acc[category] = items.length;
      return acc;
    }, {});
  }, [selectedItems]);
  
  /**
   * FIVE S CLASSIFICATION ENGINE - Classify selected items
   * Calls backend to get:
   * - classification: isolated, structural, recovery, structural_recovery (combo)
   * - strategy_recommendation
   * - target_bureaus
   * - warnings
   */
  const classifySelectedItems = useCallback(async () => {
    if (totalCount === 0) return null;
    
    setIsClassifying(true);
    try {
      // Build items array for the decision engine
      const itemsForClassification = [];
      
      // Add accounts
      selectedItems[DISPUTE_CATEGORIES.ACCOUNTS].forEach(item => {
        itemsForClassification.push({
          type: 'account',
          tradeline: item,
          bureauScope: ['TU', 'EX', 'EQ'].filter(b => 
            item[b.toLowerCase()] || item[`${b.toLowerCase()}_reported`]
          ),
        });
      });
      
      // Add inquiries
      selectedItems[DISPUTE_CATEGORIES.INQUIRIES].forEach(item => {
        itemsForClassification.push({
          type: 'inquiry',
          inquiry: item,
        });
      });
      
      // Add public records
      selectedItems[DISPUTE_CATEGORIES.PUBLIC_RECORDS].forEach(item => {
        itemsForClassification.push({
          type: 'public_record',
          record_type: item.type || item.record_type,
          ...item,
        });
      });
      
      // Add personal info disputes (data breach route)
      selectedItems[DISPUTE_CATEGORIES.PERSONAL_INFO].forEach(item => {
        itemsForClassification.push({
          type: 'personal_info',
          ...item,
        });
      });
      
      // Call the decision engine
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const decision = await adminOpsService.evaluateDecision({
        selected_items: itemsForClassification,
        user_context: { user_id: user?.id || user?.user_id },
        package_context: { 
          dispute_type: 'factual',
          from_credit_report: true
        }
      });
      
      setClassificationResult(decision);
      return decision;
      
    } catch (error) {
      console.error('Classification error:', error);
      // Return a default classification if backend fails
      return {
        classification: 'isolated',
        strategy_recommendation: { 
          title: 'Factual Dispute', 
          key: 'factual',
          reason: 'Default classification - please review'
        },
        recommended_method: 'factual_inaccuracy',
        target_bureaus: ['Equifax', 'Experian', 'TransUnion'],
        warnings: [{
          severity: 'info',
          message: 'Classification service unavailable - using default route'
        }],
        needs_review: true
      };
    } finally {
      setIsClassifying(false);
    }
  }, [totalCount, selectedItems, user, getAuthHeaders]);
  
  /**
   * Get the wizard route based on classification
   */
  const getWizardRoute = useCallback((classification) => {
    const classificationLower = (classification || 'isolated').toLowerCase();
    
    // Map classification to wizard route
    const routeMap = {
      'isolated': '/dispute-wizard/factual/select-items',
      'structural': '/dispute-wizard/factual/select-items',
      'recovery': '/dispute-wizard/factual/select-items',
      'structural_recovery': '/dispute-wizard/factual/select-items',
      'combo': '/dispute-wizard/factual/select-items',
      'inquiry': '/dispute-wizard/factual/select-items',
      'personal_info': '/dispute-wizard/factual/select-items',
      'data_breach': '/dispute-wizard/factual/select-items',
    };
    
    return routeMap[classificationLower] || '/dispute-wizard/factual/select-items';
  }, []);
  
  /**
   * Start the bulk dispute wizard (old method - shows dialog)
   */
  const startBulkDispute = useCallback(() => {
    if (totalCount > 0) {
      setShowBulkWizard(true);
    }
  }, [totalCount]);
  
  /**
   * SMART ROUTING: Start dispute with direct navigation to recommended path
   * Returns the navigation data so the caller can use navigate()
   */
  const startSmartDispute = useCallback(async () => {
    if (totalCount === 0) return null;
    
    // Get classification from backend
    const decision = await classifySelectedItems();
    if (!decision) return null;
    
    // Get classification and strategy
    const classification = decision.generation_config?.classification || 
                          decision.damage_classification?.classification ||
                          'isolated';
    
    const strategyTitle = decision.strategy_recommendation?.title || 
                          `${classification.charAt(0).toUpperCase() + classification.slice(1)} Strategy`;
    const strategyKey = decision.strategy_recommendation?.key || `${classification}_round1`;
    
    // Build the navigation state with all preloaded data
    const navigationState = {
      // Selected items by category
      preloadedItems: {
        accounts: selectedItems[DISPUTE_CATEGORIES.ACCOUNTS],
        inquiries: selectedItems[DISPUTE_CATEGORIES.INQUIRIES],
        publicRecords: selectedItems[DISPUTE_CATEGORIES.PUBLIC_RECORDS],
        personalInfo: selectedItems[DISPUTE_CATEGORIES.PERSONAL_INFO],
        creditorContacts: selectedItems[DISPUTE_CATEGORIES.CREDITOR_CONTACTS],
      },
      // Classification result
      classification: classification,
      classificationReason: decision.damage_classification?.reason || decision.generation_config?.reason,
      // Strategy recommendation
      recommendedStrategy: {
        title: strategyTitle,
        key: strategyKey,
        reason: decision.strategy_recommendation?.reason || 
                `Based on ${totalCount} selected item(s) and current file classification.`,
      },
      // Full AI Decision
      aiDecision: decision,
      // Target bureaus
      targetBureaus: decision.target_bureaus || ['Equifax', 'Experian', 'TransUnion'],
      // Warnings
      warnings: decision.warnings || [],
      // Round number (defaults to 1 for new disputes)
      roundNumber: 1,
      // From smart routing flag
      fromSmartRouting: true,
      // Category counts for display
      categoryCounts: categoryCounts,
    };
    
    // Get the target route
    const targetRoute = getWizardRoute(classification);
    
    return {
      route: targetRoute,
      state: navigationState,
      classification: classification,
      strategyTitle: strategyTitle,
    };
  }, [totalCount, selectedItems, categoryCounts, classifySelectedItems, getWizardRoute]);
  
  /**
   * Close the bulk dispute wizard
   */
  const closeBulkWizard = useCallback(() => {
    setShowBulkWizard(false);
  }, []);
  
  const value = {
    selectedItems,
    addItem,
    removeItem,
    toggleItem,
    isItemSelected,
    clearCategory,
    clearAll,
    getCategoryCount,
    totalCount,
    allSelectedItems,
    categoryCounts,
    isTrayExpanded,
    setIsTrayExpanded,
    showBulkWizard,
    startBulkDispute,
    closeBulkWizard,
    // FIVE S Smart Routing
    classifySelectedItems,
    classificationResult,
    isClassifying,
    startSmartDispute,
    getWizardRoute,
    CATEGORIES: DISPUTE_CATEGORIES,
    LABELS: CATEGORY_LABELS
  };
  
  return (
    <DisputeTrayContext.Provider value={value}>
      {children}
    </DisputeTrayContext.Provider>
  );
};

/**
 * Hook to use the dispute tray context
 */
export const useDisputeTray = () => {
  const context = useContext(DisputeTrayContext);
  if (!context) {
    throw new Error('useDisputeTray must be used within a DisputeTrayProvider');
  }
  return context;
};

export default DisputeTrayContext;
